/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useContext } from 'react';
import PortfolioItemViewCardWithPageOverlay from '../../portfolio-item/PortfolioItemViewCardWithPageOverlay';
import TaskBoard from '../../TaskBoard';
import ChallengeContentWrapper from './ChallengeContentWrapper';
import NewlySharedInfo from './NewlySharedInfo';
import ChallengeHeader from './ChallengeHeader';
import ChallengeInfo from './ChallengeInfo';
import MobilePhaseSelect from './MobilePhaseSelect';
import ChallengeContext from './ChallengeContext';
import NewlySharedContext from '../../NewlySharedChallenges/NewlySharedContext';
import ChallengeContentLoadingSpinner from './ChallengeContentLoadingSpinner';
import useMarkCoachCommentsAsReadOnLoad from './useMarkCoachCommentsAsReadOnLoad';
import ChallengePerspectiveIndicators from './ChallengePerspectiveIndicators';
import { type Challenge, type ChallengeSummary } from '@/lib/types';
import usePolledSwrAsComplexState from '@/lib/api/usePolledSwrAsComplexState';
import ChallengeColorBar from './ChallengeColorBar';
import { PortfolioItemCloseBlockingContextProvider } from '../../portfolio-item/PortfolioItemCloseBlockingContext';
import PortfolioItemThumbnailHeader from '../../portfolio-item/PortfolioItemThumbnailHeader';
import ChallengePortfolioItemContextProvider from './ChallengePortfolioItemContextProvider';
import PortfolioItemFields from '../../portfolio-item/PortfolioItemFields';
import ModelType from '@/lib/constants/challengeModel/ModelType.enum';
import ChallengeContentOuterWrapper from './ChallengeContentOuterWrapper';
import ChallengeSummaryContext from './ChallengeSummaryContext';

type ChallengeViewProps = {
  challenge: ChallengeSummary;
  onClose: () => void;
};

const ChallengeView = ({
  challenge: challengeSummary,
  onClose,
}: ChallengeViewProps) => {
  const [isNewlyShared] = useContext(NewlySharedContext);
  const [loading, challenge, setChallenge] =
    usePolledSwrAsComplexState<Challenge>(
      `challenge-portfolio/challenges/${challengeSummary.id}`,
    );

  const [taskBoardOpen, setTaskBoardOpen] = useState(false);

  useMarkCoachCommentsAsReadOnLoad(challengeSummary, loading);

  // TODO verify this works properly (for mobilephaseselect)
  useEffect(() => {
    if (loading) {
      return;
    }

    setChallenge(challenge => {
      challenge.phase = challengeSummary.phase;
    });
  }, [loading, challengeSummary.phase]);

  // TODO better typing on challengecontext: it should be clear that it could
  // be using the challengesummary as fallback
  return (
    <PortfolioItemCloseBlockingContextProvider>
      <PortfolioItemViewCardWithPageOverlay
        variant="challenge"
        taskBoardOpen={taskBoardOpen}
        onClose={onClose}
        onCloseTaskBoard={() => setTaskBoardOpen(false)}
      >
        <ChallengeSummaryContext.Provider value={challengeSummary}>
          <ChallengeContext.Provider
            value={[
              challenge ?? (challengeSummary as unknown as Challenge),
              setChallenge,
            ]}
          >
            {taskBoardOpen && <TaskBoard />}
            <ChallengePortfolioItemContextProvider>
              <ChallengeContentOuterWrapper hidden={taskBoardOpen}>
                {isNewlyShared && <NewlySharedInfo />}
                <PortfolioItemThumbnailHeader />
                <ChallengeColorBar challengeColor={challengeSummary.color} />
                <ChallengeContentWrapper>
                  <ChallengeHeader
                    onTasksOpen={() => setTaskBoardOpen(true)}
                    loading={loading}
                  />
                  <ChallengeInfo />
                  {loading ? (
                    <ChallengeContentLoadingSpinner />
                  ) : (
                    <>
                      <ChallengePerspectiveIndicators />
                      <MobilePhaseSelect />
                      <PortfolioItemFields modelType={ModelType.CHALLENGE} />
                    </>
                  )}
                </ChallengeContentWrapper>
              </ChallengeContentOuterWrapper>
            </ChallengePortfolioItemContextProvider>
          </ChallengeContext.Provider>
        </ChallengeSummaryContext.Provider>
      </PortfolioItemViewCardWithPageOverlay>
    </PortfolioItemCloseBlockingContextProvider>
  );
};

export default ChallengeView;
